import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { toast } from 'sonner';
import { Download, Filter, X, Truck, FileText, CreditCard, Eye, Upload, Trash2, Package, ExternalLink } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

// Detail Modal for Supplier items
const DetailModal = ({ item, type, onClose }) => {
  if (!item) return null;
  
  const renderContent = () => {
    switch(type) {
      case 'po':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">PO #:</span> <span className="font-bold text-indigo-600">{item.po_number}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className={`px-2 py-1 rounded text-xs ${item.status === 'received' ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>{item.status}</span></div>
              <div><span className="text-gray-500">Date:</span> <span className="font-medium">{new Date(item.created_at).toLocaleDateString()}</span></div>
              <div><span className="text-gray-500">Delivery:</span> <span className="font-medium">{item.delivery_date || '-'}</span></div>
            </div>
            {item.items && item.items.length > 0 && (
              <div>
                <span className="text-gray-500">Items:</span>
                <table className="w-full mt-2 text-sm">
                  <thead className="bg-indigo-50"><tr><th className="p-2 text-left">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Unit</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                  <tbody>{item.items.map((it, i) => (<tr key={i} className="border-b"><td className="p-2">{it.description}</td><td className="p-2 text-right">{it.quantity}</td><td className="p-2 text-right">{it.unit || '-'}</td><td className="p-2 text-right">{(it.unit_price || 0).toLocaleString()}</td><td className="p-2 text-right font-medium">{(it.total || 0).toLocaleString()}</td></tr>))}</tbody>
                </table>
              </div>
            )}
            <div className="bg-indigo-50 p-3 rounded grid grid-cols-3 gap-2 text-right">
              <div><span className="text-gray-500">Subtotal:</span> <p className="font-medium">{(item.subtotal || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">VAT:</span> <p className="font-medium">{(item.vat_amount || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">Total:</span> <p className="font-bold text-indigo-700">{(item.total || 0).toLocaleString()} SAR</p></div>
            </div>
            {/* Attachment Section */}
            {item.attachment_url && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-500 text-sm font-medium">Attachment:</span>
                <div className="mt-2">
                  {item.attachment_url.toLowerCase().endsWith('.pdf') ? (
                    <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg">
                      <FileText className="w-10 h-10 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">PDF Document</p>
                        <p className="text-xs text-gray-500">Purchase Order attachment</p>
                      </div>
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                      >
                        <Eye className="w-4 h-4" /> View PDF
                      </a>
                    </div>
                  ) : (
                    <div className="relative">
                      <img 
                        src={`${API.replace('/api', '')}${item.attachment_url}`}
                        alt="PO attachment"
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs flex items-center gap-1 hover:bg-opacity-80"
                      >
                        <Eye className="w-3 h-3" /> View Full
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case 'bill':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">Bill #:</span> <span className="font-bold text-purple-600">{item.bill_number}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className={`px-2 py-1 rounded text-xs ${item.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>{item.status}</span></div>
              <div><span className="text-gray-500">Bill Date:</span> <span className="font-medium">{item.bill_date}</span></div>
              <div><span className="text-gray-500">Due Date:</span> <span className="font-medium">{item.due_date || '-'}</span></div>
              {item.po_id && <div><span className="text-gray-500">PO Ref:</span> <span className="font-medium">{item.po_id}</span></div>}
            </div>
            {item.items && item.items.length > 0 && (
              <div>
                <span className="text-gray-500">Items:</span>
                <table className="w-full mt-2 text-sm">
                  <thead className="bg-purple-50"><tr><th className="p-2 text-left">Description</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                  <tbody>{item.items.map((it, i) => (<tr key={i} className="border-b"><td className="p-2">{it.description}</td><td className="p-2 text-right">{it.quantity}</td><td className="p-2 text-right">{(it.unit_price || 0).toLocaleString()}</td><td className="p-2 text-right font-medium">{(it.total || 0).toLocaleString()}</td></tr>))}</tbody>
                </table>
              </div>
            )}
            <div className="bg-purple-50 p-3 rounded grid grid-cols-3 gap-2 text-right">
              <div><span className="text-gray-500">Subtotal:</span> <p className="font-medium">{(item.subtotal || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">VAT:</span> <p className="font-medium">{(item.vat_amount || 0).toLocaleString()}</p></div>
              <div><span className="text-gray-500">Total:</span> <p className="font-bold text-purple-700">{(item.total || 0).toLocaleString()} SAR</p></div>
            </div>
            {item.payments && item.payments.length > 0 && (
              <div>
                <span className="text-gray-500">Bill Payments:</span>
                <div className="mt-2 space-y-1">{item.payments.map((p, i) => (<div key={i} className="flex justify-between bg-green-50 p-2 rounded text-sm"><span>{p.date} - {p.method}</span><span className="font-bold text-green-600">{(p.amount || 0).toLocaleString()} SAR</span></div>))}</div>
              </div>
            )}
            {/* Attachment Section */}
            {item.attachment_url && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-500 text-sm font-medium">Attachment:</span>
                <div className="mt-2">
                  {item.attachment_url.toLowerCase().endsWith('.pdf') ? (
                    <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg">
                      <FileText className="w-10 h-10 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">PDF Document</p>
                        <p className="text-xs text-gray-500">Invoice attachment</p>
                      </div>
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                      >
                        <Eye className="w-4 h-4" /> View PDF
                      </a>
                    </div>
                  ) : (
                    <div className="relative">
                      <img 
                        src={`${API.replace('/api', '')}${item.attachment_url}`}
                        alt="Invoice attachment"
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs flex items-center gap-1 hover:bg-opacity-80"
                      >
                        <Eye className="w-3 h-3" /> View Full
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case 'payment':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-gray-500">Payment #:</span> <span className="font-bold text-green-600">{item.payment_no}</span></div>
              <div><span className="text-gray-500">Type:</span> <span className="font-medium">{item.payment_type}</span></div>
              <div><span className="text-gray-500">Date:</span> <span className="font-medium">{item.payment_date}</span></div>
              <div><span className="text-gray-500">Method:</span> <span className="font-medium">{item.payment_method}</span></div>
              <div><span className="text-gray-500">Amount:</span> <span className="font-bold text-green-600 text-lg">{(item.amount || 0).toLocaleString()} SAR</span></div>
              {item.reference_number && <div><span className="text-gray-500">Reference:</span> <span className="font-medium">{item.reference_number}</span></div>}
            </div>
            {item.description && <div><span className="text-gray-500">Description:</span> <p className="text-sm bg-gray-50 p-2 rounded mt-1">{item.description}</p></div>}
            {/* Attachment Section */}
            {item.attachment_url && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                <span className="text-gray-500 text-sm font-medium">Attachment:</span>
                <div className="mt-2">
                  {item.attachment_url.toLowerCase().endsWith('.pdf') ? (
                    <div className="flex items-center gap-3 bg-red-50 p-3 rounded-lg">
                      <FileText className="w-10 h-10 text-red-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">PDF Document</p>
                        <p className="text-xs text-gray-500">Payment attachment</p>
                      </div>
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                      >
                        <Eye className="w-4 h-4" /> View PDF
                      </a>
                    </div>
                  ) : (
                    <div className="relative">
                      <img 
                        src={`${API.replace('/api', '')}${item.attachment_url}`}
                        alt="Payment attachment"
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <a
                        href={`${API.replace('/api', '')}${item.attachment_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded text-xs flex items-center gap-1 hover:bg-opacity-80"
                      >
                        <Eye className="w-3 h-3" /> View Full
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return <p>No details available</p>;
    }
  };

  const titles = { po: 'Purchase Order Details', bill: 'Supplier Bill Details', payment: 'Payment Details' };
  const colors = { po: 'indigo', bill: 'purple', payment: 'green' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className={`bg-${colors[type]}-600 text-white p-4 rounded-t-xl flex justify-between items-center`}>
          <h3 className="text-lg font-bold">{titles[type]}</h3>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{renderContent()}</div>
      </div>
    </div>
  );
};

const SupplierReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierStats, setSupplierStats] = useState({});
  const [summaryBySupplier, setSummaryBySupplier] = useState({});  // id → engine summary row
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [quoteTitle, setQuoteTitle] = useState('');

  const openDetail = (item, type) => { setSelectedItem(item); setSelectedItemType(type); };
  const closeDetail = () => { setSelectedItem(null); setSelectedItemType(null); };

  // Upload price quote for supplier
  const handleUploadQuote = async (supplierId, file) => {
    if (!file || !quoteTitle.trim()) {
      toast.error('Please enter a title for the quote');
      return;
    }
    
    try {
      setUploadingQuote(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', quoteTitle);
      
      await axiosInstance.post(`/suppliers/${supplierId}/upload-quote`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Price quote uploaded successfully');
      setQuoteTitle('');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to upload quote');
    } finally {
      setUploadingQuote(false);
    }
  };

  // Delete price quote
  const handleDeleteQuote = async (supplierId, quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      await axiosInstance.delete(`/suppliers/${supplierId}/quote/${quoteId}`);
      toast.success('Quote deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const suppliersRes = await axiosInstance.get('/suppliers');
      const suppliersList = suppliersRes.data;
      setSuppliers(suppliersList);

      // ─── Subledger summary (new engine — one call for all suppliers)
      try {
        const params = new URLSearchParams();
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        const qs = params.toString();
        const sumUrl = `/accounting/supplier-statement-summary${qs ? '?' + qs : ''}`;
        const sumRes = await axiosInstance.get(sumUrl);
        const map = {};
        (sumRes.data?.rows || []).forEach((r) => { map[r.id] = r; });
        setSummaryBySupplier(map);
      } catch (e) {
        setSummaryBySupplier({});
      }

      // Fetch stats for each supplier
      const statsPromises = suppliersList.map(async (supplier) => {
        try {
          let url = `/suppliers/${supplier.id}/history`;
          const params = new URLSearchParams();
          if (dateFrom) params.append('date_from', dateFrom);
          if (dateTo) params.append('date_to', dateTo);
          if (params.toString()) url += `?${params.toString()}`;

          const res = await axiosInstance.get(url);
          return { id: supplier.id, stats: res.data.stats, data: res.data };
        } catch (err) {
          return { id: supplier.id, stats: {}, data: {} };
        }
      });

      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(s => { statsMap[s.id] = s; });
      setSupplierStats(statsMap);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    fetchData();
    setShowFilter(false);
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    fetchData();
    setShowFilter(false);
  };

  // Calculate totals
  const totals = {
    bills: 0,
    totalAmount: 0,
    paid: 0,
    balance: 0
  };

  suppliers.forEach(s => {
    const stats = supplierStats[s.id]?.stats || {};
    totals.bills += stats.total_bills || 0;
    totals.totalAmount += stats.total_amount || 0;
    totals.paid += stats.total_paid || 0;
    totals.balance += stats.balance_due || 0;
  });

  const downloadPDF = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(37, 99, 235);
    doc.text('Supplier Report', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText('تقرير الموردين'), pageWidth / 2, 22, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, { align: 'center' });
    if (dateFrom || dateTo) {
      doc.text(`Period: ${dateFrom || 'Start'} to ${dateTo || 'Current'}`, pageWidth / 2, 33, { align: 'center' });
    }

    // Table
    const tableData = suppliers.map(s => {
      const stats = supplierStats[s.id]?.stats || {};
      return [
        s.supplier_number,
        s.name_english || s.name_arabic,
        stats.total_bills || 0,
        (stats.total_amount || 0).toLocaleString(),
        (stats.total_paid || 0).toLocaleString(),
        (stats.balance_due || 0).toLocaleString()
      ];
    });

    // Add totals row
    tableData.push([
      '', 'TOTAL',
      totals.bills,
      totals.totalAmount.toLocaleString(),
      totals.paid.toLocaleString(),
      totals.balance.toLocaleString()
    ]);

    autoTable(doc, {
      startY: 40,
      head: [['Supplier #', 'Name', 'Bills', 'Total Amount (SAR)', 'Paid (SAR)', 'Balance (SAR)']],
      body: tableData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235] },
      footStyles: { fillColor: [243, 244, 246], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    doc.save('Supplier_Report.pdf');
    toast.success('PDF downloaded');
  };

  if (loading) {
    return <div className="text-center py-12">Loading report...</div>;
  }

  return (
    <div data-testid="supplier-report-page">
      <div className="page-header">
        <div>
          <h2 className="page-title">Supplier Report</h2>
          <p className="text-gray-500 text-sm">تقرير الموردين</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setShowFilter(!showFilter)}
              className={`btn-secondary flex items-center gap-2 ${(dateFrom || dateTo) ? 'ring-2 ring-blue-500' : ''}`}
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>
            
            {showFilter && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 w-72">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">Filter by Date</h4>
                  <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">From</label>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">To</label>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleApplyFilter} className="btn-primary flex-1 text-sm">Apply</button>
                    <button onClick={handleClearFilter} className="btn-secondary text-sm">Clear</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button onClick={downloadPDF} className="btn-primary flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-blue-600" />
            <p className="text-blue-600 text-sm font-medium">Suppliers</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{suppliers.length}</p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-purple-600" />
            <p className="text-purple-600 text-sm font-medium">Total Bills</p>
          </div>
          <p className="text-2xl font-bold text-purple-700">{totals.bills}</p>
        </div>
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
          <p className="text-indigo-600 text-sm font-medium mb-2">Total Amount</p>
          <p className="text-xl font-bold text-indigo-700">{totals.totalAmount.toLocaleString()}</p>
          <p className="text-xs text-indigo-500">SAR</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            <p className="text-green-600 text-sm font-medium">Total Paid</p>
          </div>
          <p className="text-xl font-bold text-green-700">{totals.paid.toLocaleString()}</p>
          <p className="text-xs text-green-500">SAR</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
          <p className="text-red-600 text-sm font-medium mb-2">Total Balance</p>
          <p className="text-xl font-bold text-red-700">{totals.balance.toLocaleString()}</p>
          <p className="text-xs text-red-500">SAR Due</p>
        </div>
      </div>

      {/* Date Range Info */}
      {(dateFrom || dateTo) && (
        <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm text-blue-700">
          <strong>Period:</strong> {dateFrom || 'Start'} to {dateTo || 'Current'}
        </div>
      )}

      {/* Table + Side Panel */}
      <div className="flex gap-4">
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${selectedSupplier ? 'w-1/2' : 'w-full'}`}>
          <table className="w-full">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold">Supplier #</th>
                <th className="px-3 py-3 text-left text-xs font-semibold">Name</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">Bills</th>
                <th className="px-3 py-3 text-right text-xs font-semibold">Amount</th>
                <th className="px-3 py-3 text-right text-xs font-semibold">Paid</th>
                <th className="px-3 py-3 text-right text-xs font-semibold" title="دفعات بانتظار الاعتماد — لا تؤثر على الرصيد المحاسبي حتى الاعتماد">
                  Pending
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold">Balance</th>
                <th className="px-3 py-3 text-right text-xs font-semibold" title="120+ days overdue">120+ d</th>
                <th className="px-3 py-3 text-center text-xs font-semibold">Statement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {suppliers.map((supplier, idx) => {
                const stats = supplierStats[supplier.id]?.stats || {};
                const sum = summaryBySupplier[supplier.id] || {};
                const billed   = (sum.total_billed   != null) ? sum.total_billed   : (stats.total_amount || 0);
                const paid     = (sum.total_paid     != null) ? sum.total_paid     : (stats.total_paid   || 0);
                const closing  = (sum.closing_balance!= null) ? sum.closing_balance: (stats.balance_due  || 0);
                const overdue120 = sum.aging?.d_120_plus || 0;
                const pendingAmt = sum.pending_total || 0;
                const pendingCnt = sum.pending_count || 0;
                const isSelected = selectedSupplier?.id === supplier.id;
                return (
                  <tr
                    key={supplier.id}
                    onClick={() => setSelectedSupplier(isSelected ? null : { ...supplier, ...supplierStats[supplier.id] })}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${isSelected ? 'bg-blue-100 border-l-4 border-blue-500' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    data-testid={`supplier-report-row-${supplier.supplier_number || supplier.id}`}
                  >
                    <td className="px-3 py-3 font-medium text-blue-600">{supplier.supplier_number}</td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-sm">{supplier.name_english}</p>
                    </td>
                    <td className="px-3 py-3 text-center text-sm">{stats.total_bills || 0}</td>
                    <td className="px-3 py-3 text-right text-sm font-medium text-indigo-600">{billed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-3 text-right text-sm font-medium text-green-600">{paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td
                      className={`px-3 py-3 text-right text-sm ${pendingAmt > 0 ? 'font-bold text-yellow-700 bg-yellow-50' : 'text-gray-400'}`}
                      title={pendingCnt > 0 ? `${pendingCnt} دفعة بانتظار الاعتماد` : ''}
                      data-testid={`supplier-report-pending-${supplier.supplier_number || supplier.id}`}
                    >
                      {pendingAmt > 0
                        ? `${pendingAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${pendingCnt})`
                        : '—'}
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-bold text-red-600">{closing.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className={`px-3 py-3 text-right text-sm font-bold ${overdue120 > 0 ? 'text-red-700 bg-red-50' : 'text-gray-400'}`}>
                      {overdue120 > 0 ? overdue120.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        data-testid={`supplier-report-open-statement-${supplier.supplier_number || supplier.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/supplier-statement/${supplier.id}`); }}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-600 hover:text-white transition"
                      >
                        <ExternalLink className="w-3 h-3" /> Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-100 font-bold">
              <tr>
                <td className="px-3 py-3 text-sm" colSpan="2">TOTAL</td>
                <td className="px-3 py-3 text-center text-sm">{totals.bills}</td>
                <td className="px-3 py-3 text-right text-sm text-indigo-600">{totals.totalAmount.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-sm text-green-600">{totals.paid.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-sm text-yellow-700">
                  {(() => {
                    const pTotal = Object.values(summaryBySupplier).reduce((acc, r) => acc + (r.pending_total || 0), 0);
                    const pCount = Object.values(summaryBySupplier).reduce((acc, r) => acc + (r.pending_count || 0), 0);
                    return pTotal > 0
                      ? `${pTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${pCount})`
                      : '—';
                  })()}
                </td>
                <td className="px-3 py-3 text-right text-sm text-red-600">{totals.balance.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-sm text-red-700">
                  {Object.values(summaryBySupplier).reduce((acc, r) => acc + (r.aging?.d_120_plus || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-3 text-center text-xs text-gray-400">—</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Supplier Details Side Panel */}
        {selectedSupplier && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-5 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm text-blue-600 font-semibold">{selectedSupplier.supplier_number}</p>
                <h3 className="text-xl font-bold text-gray-900">{selectedSupplier.name_english}</h3>
                {selectedSupplier.name_arabic && <p className="text-gray-600" dir="rtl">{selectedSupplier.name_arabic}</p>}
              </div>
              <button onClick={() => setSelectedSupplier(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Financial Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-4">
              <div className="grid grid-cols-4 gap-2 text-center text-sm">
                <div><p className="text-lg font-bold text-purple-600">{selectedSupplier.stats?.total_bills || 0}</p><p className="text-xs text-gray-500">Bills</p></div>
                <div><p className="text-lg font-bold text-indigo-600">{(selectedSupplier.stats?.total_amount || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Amount</p></div>
                <div><p className="text-lg font-bold text-green-600">{(selectedSupplier.stats?.total_paid || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Paid</p></div>
                <div><p className="text-lg font-bold text-red-600">{(selectedSupplier.stats?.balance_due || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Balance</p></div>
              </div>
            </div>

            {/* All Purchase Orders */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-indigo-100 p-2 rounded">
                <FileText className="w-4 h-4 text-indigo-600" /> Purchase Orders ({selectedSupplier.data?.purchase_orders?.length || 0})
              </h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedSupplier.data?.purchase_orders?.length > 0 ? selectedSupplier.data.purchase_orders.map((po, i) => (
                  <div key={i} onClick={() => openDetail(po, 'po')} className="flex justify-between items-center bg-indigo-50 rounded p-2 text-sm cursor-pointer hover:bg-indigo-100 transition-colors">
                    <div>
                      <p className="font-medium text-indigo-700">{po.po_number}</p>
                      <p className="text-xs text-gray-500">{po.status} - {new Date(po.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="font-bold text-indigo-600">{(po.total || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No purchase orders</p>}
              </div>
            </div>

            {/* All Supplier Bills */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-purple-100 p-2 rounded">
                <FileText className="w-4 h-4 text-purple-600" /> Supplier Bills ({selectedSupplier.data?.supplier_bills?.length || 0})
              </h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedSupplier.data?.supplier_bills?.length > 0 ? selectedSupplier.data.supplier_bills.map((bill, i) => (
                  <div key={i} onClick={() => openDetail(bill, 'bill')} className="flex justify-between items-center bg-purple-50 rounded p-2 text-sm cursor-pointer hover:bg-purple-100 transition-colors">
                    <div>
                      <p className="font-medium text-purple-700">{bill.bill_number}</p>
                      <p className="text-xs text-gray-500">{bill.status} - {bill.bill_date}</p>
                    </div>
                    <span className="font-bold text-purple-600">{(bill.total || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No supplier bills</p>}
              </div>
            </div>

            {/* All Payments */}
            <div className="mb-2">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-green-100 p-2 rounded">
                <CreditCard className="w-4 h-4 text-green-600" /> Payments ({selectedSupplier.data?.payments?.length || 0})
              </h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedSupplier.data?.payments?.length > 0 ? selectedSupplier.data.payments.map((p, i) => (
                  <div key={i} onClick={() => openDetail(p, 'payment')} className="flex justify-between items-center bg-green-50 rounded p-2 text-sm cursor-pointer hover:bg-green-100 transition-colors">
                    <div>
                      <p className="font-medium text-green-700">{p.payment_no}</p>
                      <p className="text-xs text-gray-500">{p.payment_method} - {p.payment_date}</p>
                    </div>
                    <span className="font-bold text-green-600">{(p.amount || 0).toLocaleString()}</span>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No payments</p>}
              </div>
            </div>

            {/* Materials Section */}
            {selectedSupplier.materials && (
              <div className="mb-2">
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-amber-100 p-2 rounded">
                  <Package className="w-4 h-4 text-amber-600" /> Materials (المواد)
                </h4>
                <div className="bg-amber-50 rounded p-3 text-sm">
                  <p className="text-gray-700 whitespace-pre-wrap" dir="auto">{selectedSupplier.materials}</p>
                </div>
              </div>
            )}

            {/* Price Quotes Section */}
            <div className="mb-2">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 bg-orange-100 p-2 rounded">
                <FileText className="w-4 h-4 text-orange-600" /> Price Quotes (عروض الأسعار) ({selectedSupplier.price_quotes?.length || 0})
              </h4>
              
              {/* Upload New Quote */}
              <div className="bg-orange-50 rounded p-3 mb-2">
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="Quote title (Required) - عنوان عرض السعر"
                    value={quoteTitle}
                    onChange={(e) => setQuoteTitle(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm mb-2"
                  />
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    className="w-full px-2 py-2 border rounded text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-orange-600 file:text-white file:cursor-pointer file:text-xs"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        if (!quoteTitle.trim()) {
                          toast.error('Please enter a quote title first');
                          e.target.value = '';
                          return;
                        }
                        handleUploadQuote(selectedSupplier.id, e.target.files[0]);
                        e.target.value = '';
                      }
                    }}
                    disabled={uploadingQuote}
                  />
                </div>
                {uploadingQuote && (
                  <p className="text-orange-600 text-xs flex items-center gap-1">
                    <span className="animate-spin">⏳</span> Uploading...
                  </p>
                )}
              </div>

              {/* Quotes List */}
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedSupplier.price_quotes?.length > 0 ? selectedSupplier.price_quotes.map((quote, i) => (
                  <div key={quote.id || i} className="flex justify-between items-center bg-orange-50 rounded p-2 text-sm">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-orange-600" />
                      <div>
                        <p className="font-medium text-orange-700">{quote.title}</p>
                        <p className="text-xs text-gray-500">{new Date(quote.uploaded_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <a
                        href={`${API.replace('/api', '')}${quote.file_url.startsWith('/api') ? quote.file_url : '/api' + quote.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 bg-orange-600 text-white rounded hover:bg-orange-700"
                        title="View PDF"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                      <a
                        href={`${API.replace('/api', '')}${quote.file_url.startsWith('/api') ? quote.file_url : '/api' + quote.file_url}`}
                        download
                        className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDeleteQuote(selectedSupplier.id, quote.id)}
                        className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )) : <p className="text-gray-400 text-sm text-center py-2">No price quotes uploaded</p>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && <DetailModal item={selectedItem} type={selectedItemType} onClose={closeDetail} />}
    </div>
  );
};

export default SupplierReport;
